import { Injectable } from '@angular/core';
import * as CookieConsent from 'vanilla-cookieconsent';
import { AppInsightsService } from './app-insights.service';
import { ClarityService } from './clarity.service';

const ANALYTICS_CATEGORY = 'analytics';

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  constructor(
    private clarityService: ClarityService,
    private appInsightsService: AppInsightsService
  ) {}

  init(): void {
    if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
      return;
    }

    CookieConsent.run({
      guiOptions: {
        consentModal: {
          layout: 'cloud inline',
          position: 'bottom center',
          equalWeightButtons: true,
        },
        preferencesModal: {
          layout: 'box',
          equalWeightButtons: true,
        },
      },

      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          // https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-cookies
          autoClear: {
            cookies: [
              { name: /^_cl/ },
              { name: 'CLID' },
              { name: 'ANONCHK' },
              { name: 'MR' },
              { name: 'MUID' },
              { name: 'SM' },
              { name: /^ai_/ },
            ],
          },
        },
      },

      onConsent: () => {
        this.handleConsentChange();
      },

      onChange: () => {
        this.handleConsentChange();
      },

      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We use cookies',
              description:
                'We use cookies to improve your experience and analyze site usage. You can choose which cookies you allow.',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              showPreferencesBtn: 'Manage preferences',
            },
            preferencesModal: {
              title: 'Cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Save preferences',
              closeIconLabel: 'Close',
              sections: [
                {
                  title: 'Cookie usage',
                  description:
                    'We use cookies to ensure basic site functionality and to improve your experience. You can choose to opt in or out of each category at any time.',
                },
                {
                  title: 'Strictly necessary cookies',
                  description:
                    'These cookies are essential for the website to function properly and cannot be disabled.',
                  linkedCategory: 'necessary',
                },
                {
                  title: 'Analytics cookies',
                  description:
                    'These cookies help us understand how visitors interact with the website by collecting anonymous usage data.',
                  linkedCategory: ANALYTICS_CATEGORY,
                  cookieTable: {
                    headers: {
                      name: 'Cookie',
                      description: 'Description',
                    },
                    body: [
                      {
                        name: '_clck',
                        description: 'Microsoft Clarity user identifier, persists across sessions.',
                      },
                      {
                        name: '_clsk',
                        description:
                          'Microsoft Clarity session identifier, connects page views within a session.',
                      },
                      {
                        name: 'CLID',
                        description:
                          'Microsoft Clarity identifier used for cross-session tracking.',
                      },
                      {
                        name: 'ai_user',
                        description:
                          'Application Insights unique user identifier, persists across sessions.',
                      },
                      {
                        name: 'ai_session',
                        description: 'Application Insights session identifier.',
                      },
                    ],
                  },
                },
              ],
            },
          },
        },
      },
    });
  }

  private handleConsentChange(): void {
    if (CookieConsent.acceptedCategory(ANALYTICS_CATEGORY)) {
      this.clarityService.grantConsent();
      this.appInsightsService.grantConsent();
    } else {
      this.clarityService.denyConsent();
      this.appInsightsService.denyConsent();
    }
  }
}
